<template>
    <v-card class="mt-6">
        <v-card-title>
            {{ $t('labels.stores') }}
            <v-spacer></v-spacer>
            <v-btn color="success" small @click="openDialog(null)">
                <v-icon left>add</v-icon> {{ $t('actions.new_store') }}
            </v-btn>
        </v-card-title>
        <v-data-table
            :headers="headers"
            :items="stores"
            :loading="loading"
            hide-default-footer
            disable-pagination
        >
            <template #[`item.restaurants`]="{ item }">
                <status-badge
                    v-for="restaurant in item.restaurants"
                    :key="restaurant.id"
                    class="mr-1 my-1"
                    tone="neutral"
                >
                    {{ restaurant.id }} – {{ restaurant.name }}
                </status-badge>
            </template>
            <template #[`item.actions`]="{ item }">
                <edit-button @execute="openDialog(item)"></edit-button>
                <delete-action
                    :entity-name="item.name"
                    :entity-id="item.id"
                    :api="storesApi"
                    @deleted="onDeleted"
                ></delete-action>
            </template>
        </v-data-table>
        <store-dialog
            v-if="dialogOpen"
            :brand-id="brandId"
            :store="editedStore"
            @saved="onSaved"
            @deleted="onDeleted"
            @closed="dialogOpen = false"
        ></store-dialog>
    </v-card>
</template>

<script>
import RestApiCollection from '@/api/RestApiCollection'
import RestApiType from '@/api/RestApiType'
import SelectApiClientType from '@/api/SelectApiClientType'
import selectapiType from '@/store/type/selectapiType'
import EditButton from '@/components/table/buttons/EditButton.vue'
import DeleteAction from '@/components/table/actions/DeleteAction.vue'
import Notification from '@/services/Notification/Notification'
import StoreDialog from './StoreDialog.vue'
import StatusBadge from '@/components/elements/StatusBadge.vue'

export default {
    components: {
        EditButton,
        DeleteAction,
        StoreDialog,
        StatusBadge,
    },
    props: {
        brandId: {
            type: Number,
            required: true,
        },
        reload: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            storesApi: RestApiType.STORES,
            stores: [],
            loading: false,
            loadToken: 0,
            dialogOpen: false,
            editedStore: null,
        }
    },
    computed: {
        headers() {
            return [
                { value: 'name', text: this.$t('labels.name') },
                {
                    value: 'restaurants',
                    text: this.$t('labels.restaurants'),
                    sortable: false,
                },
                { value: 'actions', text: '', sortable: false, width: 110 },
            ]
        },
    },
    watch: {
        reload: 'load',
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.loadToken = (this.loadToken || 0) + 1
            const token = this.loadToken

            this.loading = true
            return RestApiCollection.get(RestApiType.STORES)
                .list({ 'restaurants.brand': this.brandId })
                .then((data) => {
                    if (token !== this.loadToken) {
                        // A newer load superseded this one; ignore the stale response.
                        return
                    }
                    this.stores = data['hydra:member'] || []
                    this.$emit('loaded', this.stores)
                })
                .catch((reason) => {
                    Notification.error(
                        this.$t('labels.stores'),
                        reason?.response?.data?.['hydra:description'] ||
                            reason?.message ||
                            'Request failed'
                    )
                    throw reason
                })
                .finally(() => {
                    if (token === this.loadToken) {
                        this.loading = false
                    }
                })
        },
        openDialog(store) {
            this.editedStore = store
            this.dialogOpen = true
        },
        onSaved() {
            this.dialogOpen = false
            this.afterMutation()
        },
        onDeleted() {
            this.dialogOpen = false
            // AbstractRestApi.delete() never refreshes select caches – do it here
            this.$store.dispatch(
                selectapiType.getActionName(
                    SelectApiClientType.RESTAURANT,
                    selectapiType.actions.REFRESH
                )
            )
            this.afterMutation()
        },
        afterMutation() {
            this.load().then(() => this.$emit('changed'))
        },
    },
}
</script>
